import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Building2,
  Loader2,
  CheckCircle2,
  ChevronRight,
  Plus,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { propertyApi, unitApi } from '@/lib/api';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { RegistryStatusCard } from '@/components/properties/RegistryStatusCard';
import { useJsApiLoader } from '@react-google-maps/api';

interface AddPropertyDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 1 | 2 | 3 | 4; // Added step 4 for Success/Registry Result

interface UnitConfig {
  id: string; // temp id for list management
  name: string; // e.g., "Unit 1", "Basement"
  bedrooms: number;
  bathrooms: number;
  monthlyRent: number;
  sqFt: number;
}

export default function AddPropertyDialog({ isOpen, onClose }: AddPropertyDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creationLog, setCreationLog] = useState<string[]>([]);

  // Registry status state
  const [newPropertyId, setNewPropertyId] = useState<string | null>(null);
  const [registryStatus, setRegistryStatus] = useState<{
    isRegistered: boolean;
    pid?: string;
    registrationNumber?: string;
    unitCount?: number;
    message: string;
  } | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'],
  });

  // Step 1: Property Details
  const [propertyData, setPropertyData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: 'NS',
    postalCode: '',
    country: 'Canada',
    pid: '', // Added PID

    type: 'House',
    propertyType: 'MultiUnit', // 'SingleFamily' | 'MultiUnit'
    rentalMode: 'LongTerm', // 'LongTerm' | 'ShortTerm' | 'Hybrid'
  });

  // Step 2: Units
  // Default to 1 unit
  const [units, setUnits] = useState<UnitConfig[]>([
    { id: '1', name: 'Unit 1', bedrooms: 2, bathrooms: 1, monthlyRent: 1500, sqFt: 800 }
  ]);


  const resetForm = () => {
    setStep(1);
    setIsSubmitting(false);
    setCreationLog([]);
    setNewPropertyId(null);
    setRegistryStatus(null);
    setPropertyData({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      province: 'NS',
      postalCode: '',
      country: 'Canada',
      pid: '',
      type: 'House',
      propertyType: 'MultiUnit',
      rentalMode: 'LongTerm',
    });
    setUnits([
      { id: '1', name: 'Unit 1', bedrooms: 2, bathrooms: 1, monthlyRent: 1500, sqFt: 800 }
    ]);
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    setCreationLog(['Creating property...']);

    try {
      const propResponse = await propertyApi.create({
        ...propertyData,
        propertyType: propertyData.propertyType as 'MultiUnit' | 'SingleFamily',
        unitCount: propertyData.propertyType === 'SingleFamily' ? 1 : units.length,
        pid: propertyData.pid || undefined // Only send if populated
      });

      const propertyId = propResponse.data.id;
      setNewPropertyId(propertyId);

      if (propResponse.data.registryStatus) {
        setRegistryStatus(propResponse.data.registryStatus);
      }

      setCreationLog(prev => [...prev, 'Property created successfully.']);

      if (propertyData.propertyType === 'MultiUnit') {
        setCreationLog(prev => [...prev, `Creating ${units.length} units...`]);
        // 2. Create Units sequentially
        // ... (existing unit creation logic)

        // Logic for MultiUnit unit creation
        let createdCount = 0;
        for (const unit of units) {
          try {
            await unitApi.create(propertyId, {
              unitNumber: unit.name,
              bedrooms: unit.bedrooms,
              bathrooms: unit.bathrooms,
              monthlyRent: unit.monthlyRent,
              squareFootage: unit.sqFt,
              name: unit.name
            });
            createdCount++;
          } catch (err) {
            console.error(`Failed to create unit ${unit.name}`, err);
            setCreationLog(prev => [...prev, `❌ Failed to create ${unit.name}`]);
          }
        }
        setCreationLog(prev => [...prev, `Done. ${createdCount}/${units.length} units created.`]);
      } else {
        setCreationLog(prev => [...prev, 'Default unit created automatically for Single Family Home.']);
      }

      // Success logic modified to show step 4
      queryClient.invalidateQueries({ queryKey: ['properties'] });

      setCreationLog(prev => [...prev, '✅ All done! Verifying registry status...']);

      // Short delay to show success log then move to Step 4
      setTimeout(() => {
        setIsSubmitting(false);
        setStep(4);
      }, 1000);

    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to create property');
      setCreationLog(prev => [...prev, '❌ Property creation failed.']);
      setIsSubmitting(false); // Stop logs so user can retry or cancel
    }
  };

  const addUnit = () => {
    const nextId = (units.length + 1).toString();
    setUnits([
      ...units,
      {
        id: crypto.randomUUID(),
        name: `Unit ${nextId}`,
        bedrooms: 1,
        bathrooms: 1,
        monthlyRent: 1200,
        sqFt: 600
      }
    ]);
  };

  const removeUnit = (id: string) => {
    if (units.length <= 1) return;
    setUnits(units.filter(u => u.id !== id));
  };

  const updateUnit = (id: string, field: keyof UnitConfig, value: any) => {
    setUnits(units.map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={() => !isSubmitting && step !== 4 && onClose()}
      />

      <div className="relative flex w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add Property</h2>
              {step < 4 && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className={cn(step >= 1 ? "text-primary-600 font-medium" : "")}>Details</span>
                  <span className={cn(step >= 1 ? "text-primary-600 font-medium" : "")}>Details</span>
                  {propertyData.propertyType === 'MultiUnit' && (
                    <>
                      <ChevronRight className="h-3 w-3" />
                      <span className={cn(step >= 2 ? "text-primary-600 font-medium" : "")}>Units</span>
                    </>
                  )}
                  <ChevronRight className="h-3 w-3" />
                  <span className={cn(step >= 3 ? "text-primary-600 font-medium" : "")}>Review</span>
                </div>
              )}
              {step === 4 && (
                <p className="text-sm text-green-600 font-medium">🎉 Implementation Complete</p>
              )}
            </div>
          </div>
          {!isSubmitting && step !== 4 && (
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {isSubmitting ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Creating Property...</h3>
              <div className="mt-4 w-full max-w-xs space-y-2 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                {creationLog.map((log, i) => (
                  <p key={i} className="flex items-center gap-2">
                    {log.includes('successfully') ? <CheckCircle2 className="h-3 w-3 text-green-500" /> : <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />}
                    {log}
                  </p>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: Details */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">Property Name</label>
                      <input
                        type="text"
                        value={propertyData.name}
                        onChange={e => setPropertyData({ ...propertyData, name: e.target.value })}
                        placeholder="e.g., Downtown Apartments"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        autoFocus
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Address Line 1
                      </label>
                      {isLoaded ? (
                        <AddressAutocomplete
                          defaultValue={propertyData.addressLine1}
                          onAddressSelect={(data) => {
                            setPropertyData(prev => ({
                              ...prev,
                              addressLine1: data.addressLine1,
                              city: data.city,
                              province: data.province || prev.province,
                              postalCode: data.postalCode,
                              country: data.country
                            }));
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={propertyData.addressLine1}
                          onChange={e => setPropertyData({ ...propertyData, addressLine1: e.target.value })}
                          placeholder="123 Main St"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        />
                      )}
                    </div>

                    {/* PID Input - New */}
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Property Identifier (PID)
                        <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={propertyData.pid}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setPropertyData({ ...propertyData, pid: val });
                        }}
                        placeholder="e.g. 12345678 (8 digits)"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter the 8-digit PID to automatically verify registry status with Halifax Open Data.
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        value={propertyData.city}
                        onChange={e => setPropertyData({ ...propertyData, city: e.target.value })}
                        placeholder="Halifax"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Province</label>
                      <select
                        value={propertyData.province}
                        onChange={e => setPropertyData({ ...propertyData, province: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="NS">Nova Scotia</option>
                        <option value="NB">New Brunswick</option>
                        <option value="PE">Prince Edward Island</option>
                        <option value="NL">Newfoundland</option>
                        <option value="ON">Ontario</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Postal Code</label>
                      <input
                        type="text"
                        value={propertyData.postalCode}
                        onChange={e => setPropertyData({ ...propertyData, postalCode: e.target.value })}
                        placeholder="B3J 2K9"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                      <select
                        value={propertyData.type}
                        onChange={e => setPropertyData({ ...propertyData, type: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="House">House</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Condo">Condo</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Duplex">Duplex</option>
                        <option value="MultiFamily">Multi-Family</option>
                      </select>
                    </div>

                    <div>  <label className="mb-1 block text-sm font-medium text-gray-700">Property Structure</label>
                      <select
                        value={propertyData.propertyType}
                        onChange={e => setPropertyData({ ...propertyData, propertyType: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="MultiUnit">Multi-Unit (Apartment, Duplex)</option>
                        <option value="SingleFamily">Single Family Home</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Rental Strategy</label>
                      <select
                        value={propertyData.rentalMode}
                        onChange={e => setPropertyData({ ...propertyData, rentalMode: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="LongTerm">Long Term Leasing (Standard)</option>
                        <option value="ShortTerm">Short Term Rental (Airbnb style)</option>
                        <option value="Hybrid">Hybrid / Seasonal</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Units */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Define Units</h3>
                      <p className="text-xs text-gray-500">Add details for each unit in this property</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={addUnit} className="gap-2">
                      <Plus className="h-3 w-3" />
                      Add Unit
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {units.map((unit) => (
                      <div key={unit.id} className="relative rounded-xl border border-gray-200 bg-gray-50 p-4 transition-all hover:border-gray-300">
                        <div className="grid gap-4 sm:grid-cols-12">
                          <div className="sm:col-span-3">
                            <label className="mb-1 block text-xs font-medium text-gray-500">Unit Name</label>
                            <input
                              type="text"
                              value={unit.name}
                              onChange={e => updateUnit(unit.id, 'name', e.target.value)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="mb-1 block text-xs font-medium text-gray-500">Bedrooms</label>
                            <input
                              type="number"
                              min="0"
                              value={unit.bedrooms}
                              onChange={e => updateUnit(unit.id, 'bedrooms', parseInt(e.target.value) || 0)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="mb-1 block text-xs font-medium text-gray-500">Bathrooms</label>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={unit.bathrooms}
                              onChange={e => updateUnit(unit.id, 'bathrooms', parseFloat(e.target.value) || 0)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="mb-1 block text-xs font-medium text-gray-500">Rent ($)</label>
                            <input
                              type="number"
                              min="0"
                              value={unit.monthlyRent}
                              onChange={e => updateUnit(unit.id, 'monthlyRent', parseInt(e.target.value) || 0)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="mb-1 block text-xs font-medium text-gray-500">Size (sqft)</label>
                            <input
                              type="number"
                              min="0"
                              value={unit.sqFt}
                              onChange={e => updateUnit(unit.id, 'sqFt', parseInt(e.target.value) || 0)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="flex items-end justify-end sm:col-span-1">
                            <button
                              onClick={() => removeUnit(unit.id)}
                              disabled={units.length === 1}
                              className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <h3 className="font-semibold text-gray-900">{propertyData.name}</h3>
                    <p className="text-sm text-gray-500">
                      {propertyData.addressLine1}, {propertyData.city}, {propertyData.province} {propertyData.postalCode}
                    </p>
                    <p className="mt-2 text-sm font-medium text-gray-700">Type: {propertyData.type}</p>
                    {propertyData.pid && (
                      <p className="mt-1 text-sm font-medium text-gray-700">PID: {propertyData.pid}</p>
                    )}
                  </div>

                  <div>
                    <h4 className="mb-3 text-sm font-medium text-gray-900">Units Configuration ({units.length})</h4>
                    <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
                      {units.map(unit => (
                        <div key={unit.id} className="flex items-center justify-between p-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-900">{unit.name}</span>
                            <span className="ml-2 text-gray-500">{unit.bedrooms} Bed, {unit.bathrooms} Bath</span>
                          </div>
                          <div className="font-medium text-gray-900">${unit.monthlyRent}/mo</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
                    <p>Total Monthly Revenue Potential: <span className="font-bold">${units.reduce((acc, u) => acc + u.monthlyRent, 0).toLocaleString()}</span></p>
                  </div>
                </div>
              )}

              {/* Step 4: Registry Result / Success */}
              {step === 4 && (
                <div className="space-y-6 pt-4">
                  <div className="text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">Property Created Successfully!</h3>
                    <p className="text-sm text-gray-500">
                      Your property is now added to the system.
                    </p>
                  </div>

                  {registryStatus ? (
                    <RegistryStatusCard status={registryStatus} />
                  ) : (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm text-blue-700">
                        No Registry information available. You can update this in the Compliance tab later.
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                    <h4 className="mb-2 font-medium text-gray-900">Next Steps:</h4>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>Configure compliance elements (Smoke Alarms, etc.)</li>
                      <li>Upload insurance documents</li>
                      <li>Assign tenants</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        {!isSubmitting && (
          <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4">
            {step === 4 ? (
              // Completetion Actions
              <div className="flex w-full items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    onClose();
                    resetForm();
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    // Navigate to compliance setup
                    navigate(`/dashboard/properties/${newPropertyId}/compliance?setup=true`);
                  }}
                  className="bg-primary-600 hover:bg-primary-700"
                >
                  Continue to Compliance Setup
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ) : (
              // Normal Navigation
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (step === 1) onClose();
                    else if (step === 3 && propertyData.propertyType === 'SingleFamily') setStep(1);
                    else setStep((prev) => (prev - 1) as Step);
                  }}
                >
                  {step === 1 ? 'Cancel' : 'Back'}
                </Button>

                <Button
                  onClick={() => {
                    if (step === 3) handleCreate();
                    else if (step === 1 && propertyData.propertyType === 'SingleFamily') setStep(3);
                    else setStep((prev) => (prev + 1) as Step);
                  }}
                  disabled={step === 1 && !propertyData.name}
                >
                  {step === 3 ? 'Create Property' : 'Next'}
                  {step !== 3 && <ChevronRight className="ml-2 h-4 w-4" />}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
